import { FC, Fragment } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Alert, AlertTitle, Box, Stack, Typography } from '@mui/material';

import { fetchProducts } from '@api/services/profile';
import { Avatar, DataTree, ErrorMessage, Loading } from '@components/base';
import useGenericDataContext from '@hooks/useGenericDataContext';
import useLoad from '@hooks/useLoad';
import { useProviderNames } from '@hooks/useProviderNames';
import useSubscriber from '@hooks/useSubscriber';
import { DetailedPlaybackOption, DetailedTitle } from '@models/Title';
import { PlatformId, SourceId } from '@modules/simulations/types';
import { formatDate } from '@utils/dateUtils';

import { SubscriberLoginForm } from '../SubscriberLoginForm';

import CardWithExpandableArea from './CardWithExpandableArea';

const ProductsFromProvider: FC<DetailedPlaybackOption> = ({
  platformTitleId,
  providerIdentifier,
  sourceId,
  serviceType,
}) => {
  const { t } = useTranslation();
  const { getProviderName, getProviderImage } = useProviderNames();
  const { subscriber: { subscriberId, vodPlatform } = {} } = useSubscriber();

  const { data, error, isLoading } = useLoad(
    () => fetchProducts(platformTitleId),
    ['product', { platformTitleId, subscriberId }],
    { enabled: !!platformTitleId && serviceType === 'vod' }
  );

  // Only display products for providers with serviceType = vod
  const products = serviceType === 'vod' ? data : undefined;

  const providerName = getProviderName(providerIdentifier);
  const providerImageUrl = getProviderImage(providerIdentifier);

  const isHuaweiVOD = sourceId === SourceId.HUAWEI_VOD;
  const isCVP = sourceId === SourceId.COMCAST;

  return (
    <Box>
      <Stack alignItems='center' direction='row' mb={1} spacing={1.5}>
        <Avatar size='large' src={providerImageUrl} sx={{ borderRadius: 1 }} />

        <Stack>
          <Typography component='h5' variant={isHuaweiVOD || isCVP ? 'h4' : 'h3'}>
            {providerName}
          </Typography>

          {(isHuaweiVOD || isCVP) && (
            <Typography color='text.secondary' variant='body2'>
              {t('source')}: {sourceId}
            </Typography>
          )}
        </Stack>
      </Stack>

      {isLoading ? (
        <Loading />
      ) : products && products.length > 0 ? (
        <Stack spacing={1}>
          {products.map(product => {
            const { contentType, expiration, id, price, resolution, title } = product;

            return (
              <CardWithExpandableArea
                additionalInfo={
                  expiration && (
                    <Typography variant='body2'>
                      {t('expires')} {formatDate(expiration)}
                    </Typography>
                  )
                }
                expandableContent={<DataTree data={product} disableControls language='json' />}
                heading={title}
                itemId={id}
                key={id}
                subheading={t('titleProduct.metadata', {
                  contentType: contentType.toLocaleUpperCase(),
                  price,
                  resolution,
                })}
              />
            );
          })}
        </Stack>
      ) : error && platformTitleId ? (
        <ErrorMessage error={error} />
      ) : (
        <Alert severity='info'>
          {(vodPlatform === PlatformId.CVP && isHuaweiVOD) ||
          (vodPlatform === PlatformId.Huawei && isCVP) ? (
            <Fragment>
              <AlertTitle>{t('products.empty')}</AlertTitle>
              <Typography variant='body2'>
                <Trans
                  components={[
                    <Typography
                      component='span'
                      display='inline'
                      fontStyle='italic'
                      fontWeight={500}
                      variant='inherit'
                    />,
                  ]}
                  i18nKey='content.noAccess'
                  values={{ sourceId }}
                />
              </Typography>
            </Fragment>
          ) : (
            t('products.empty')
          )}
        </Alert>
      )}
    </Box>
  );
};

const Products: FC = () => {
  const { data: { playbackOptions = [] } = {} } = useGenericDataContext<DetailedTitle>();

  return (
    <Stack spacing={4}>
      {playbackOptions.map(playbackOption => (
        <ProductsFromProvider {...playbackOption} key={JSON.stringify(playbackOption)} />
      ))}
    </Stack>
  );
};

const ProductsView: FC = () => {
  const { subscriber } = useSubscriber();
  return subscriber ? <Products /> : <SubscriberLoginForm />;
};

export default ProductsView;
